import React, { useCallback } from 'react';

import { useDispatch, useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { blockedRedirectionSelector } from '../../ducks/selectors';
import { actions as routerActions } from '../../ducks/slice';
import { routerApi } from '../../routers/router';
import { BlockedRedirectionDialog, BlockedRedirectionDialogProps } from './BlockedRedirectionDialog';

export interface ConnectedBlockedRedirectionDialogProps {
    /** description to display on the alert */
    description?: BlockedRedirectionDialogProps['description'];
    /** confirm button label to display on the alert */
    confirmLabel?: BlockedRedirectionDialogProps['confirmLabel'];
    /** cancel button label to display on the alert */
    cancelLabel?: BlockedRedirectionDialogProps['cancelLabel'];
    /** Kind of the alert dialog: "info" | "success" | "warning" | "error" */
    kind?: BlockedRedirectionDialogProps['kind'];
    /** should prevent user from leaving with browser buttons */
    isActiveOnBrowserActions?: boolean;
    /** title to display on the alert */
    title?: BlockedRedirectionDialogProps['title'];
    /** callback after unload trigger */
    beforeUnloadCallback?: () => void;
}

/**
 * This component is directly connected to the "status" field of the router slice.
 * It should be used when an action has to be made before redirecting a user.
 */
export const ConnectedBlockedRedirectionDialog: React.FC<ConnectedBlockedRedirectionDialogProps> = ({
    isActiveOnBrowserActions = false,
    beforeUnloadCallback,
    ...remainingProps
}: ConnectedBlockedRedirectionDialogProps) => {
    const dispatch = useDispatch();
    const { translateKey } = useTranslate();
    const blockedRedirection = useSelector(blockedRedirectionSelector);

    /**
     * Is the alert open (happen after a redirect)
     */
    const isOpen = React.useMemo(
        () => blockedRedirection?.blockedRoute !== undefined,
        [blockedRedirection?.blockedRoute],
    );

    /**
     * Is the blocking mechanism is activated (happend after a routerActions.blockRedirection())
     */
    const isBlockingActivated = React.useMemo(
        () => blockedRedirection?.blockingRoute !== undefined,
        [blockedRedirection?.blockingRoute],
    );

    /**
     * Add a listener on beforeunload to alert when a user
     * leave the page with unsaved changes
     */
    React.useEffect(() => {
        if (!(isActiveOnBrowserActions && isBlockingActivated && !isOpen)) {
            return () => null;
        }

        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            // Cancel the event
            event.preventDefault();

            if (beforeUnloadCallback) {
                beforeUnloadCallback();
            }

            // Old versions of Chrome requires returnValue to be set.
            // eslint-disable-next-line no-param-reassign
            event.returnValue = remainingProps.description || translateKey(GLOBAL.UNSAVED_MODIFICATIONS_DESCRIPTION);
            return event.returnValue;
        };

        window.addEventListener('beforeunload', onBeforeUnload);

        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [
        beforeUnloadCallback,
        isActiveOnBrowserActions,
        isBlockingActivated,
        isOpen,
        remainingProps.description,
        translateKey,
    ]);

    /**
     * Reblock after canceling
     */
    const onCancel = () => dispatch(routerActions.blockRedirection());

    /**
     * Redirect and unblock after confirm
     */
    const onConfirm = useCallback(() => {
        if (blockedRedirection?.blockedRoute) {
            routerApi.redirect(blockedRedirection.blockedRoute, blockedRedirection.history);
            dispatch(routerActions.unblockRedirection());
        }
    }, [blockedRedirection, dispatch]);

    return <BlockedRedirectionDialog isOpen={isOpen} onCancel={onCancel} onConfirm={onConfirm} {...remainingProps} />;
};
